import { useScrollReveal } from '../../../hooks/useScrollReveal';

const SERVICES = [
  {
    icon: 'ri-home-heart-line',
    title: 'Mediación Familiar',
    desc: 'Divorcios, custodia de menores, herencias, conflictos entre padres e hijos. Soluciones que protegen a toda la familia.',
    items: ['Divorcio de mutuo acuerdo', 'Custodia y régimen de visitas', 'Alimentos y pensiones', 'Conflictos sucesorios'],
    image: 'https://public.readdy.ai/ai/img_res/50e75d468b0c4de7cc8639c3f0c9a41e.jpg',
    tag: 'Más solicitado',
  },
  {
    icon: 'ri-briefcase-line',
    title: 'Mediación Civil y Mercantil',
    desc: 'Contratos, reclamaciones de deuda, relaciones comerciales y disputas entre empresas.',
    items: ['Incumplimiento de contratos', 'Reclamación de deudas', 'Propiedad y arrendamientos', 'Derechos de consumo'],
    image: 'https://readdy.ai/api/search-image?query=civil%20mercantile%20legal%20mediation%20professional%20business%20people%20signing%20documents%20office%20modern%20setting%20bright&width=600&height=400&seq=svc002&orientation=landscape',
    tag: 'Empresa',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Mediación Laboral',
    desc: 'Conflictos entre trabajadores y empleadores, acoso laboral, despidos y condiciones de trabajo.',
    items: ['Conflictos empresa-trabajador', 'Acoso laboral', 'Condiciones de trabajo', 'Despidos improcedentes'],
    image: 'https://readdy.ai/api/search-image?query=workplace%20labor%20mediation%20professional%20setting%20employer%20employee%20discussion%20resolution%20modern%20office%20natural%20light&width=600&height=400&seq=svc003&orientation=landscape',
    tag: 'Empresa',
  },
  {
    icon: 'ri-bank-line',
    title: 'Mediación Bancaria y Financiera',
    desc: 'Hipotecas, cláusulas abusivas, deudas con entidades financieras y seguros.',
    items: ['Cláusulas hipotecarias abusivas', 'Reestructuración de deudas', 'Reclamaciones de seguros', 'Inversiones y productos financieros'],
    image: 'https://readdy.ai/api/search-image?query=banking%20financial%20mediation%20professional%20consultation%20documents%20mortgage%20bank%20clean%20elegant%20office&width=600&height=400&seq=svc004&orientation=landscape',
    tag: 'Finanzas',
  },
  {
    icon: 'ri-school-line',
    title: 'Mediación Escolar',
    desc: 'Conflictos en centros educativos entre alumnos, padres, docentes y equipos directivos.',
    items: ['Conflictos entre alumnos', 'Relación padres-colegio', 'Bullying y acoso escolar', 'Convivencia educativa'],
    image: 'https://readdy.ai/api/search-image?query=school%20educational%20mediation%20children%20students%20adults%20conflict%20resolution%20education%20center%20bright%20classroom%20environment&width=600&height=400&seq=svc005&orientation=landscape',
    tag: 'Educación',
  },
  {
    icon: 'ri-community-line',
    title: 'Mediación Comunitaria',
    desc: 'Conflictos vecinales, comunidades de propietarios, ruidos, obras y convivencia.',
    items: ['Conflictos vecinales', 'Comunidades de propietarios', 'Ruidos y molestias', 'Gestión de espacios comunes'],
    image: 'https://readdy.ai/api/search-image?query=community%20neighborhood%20mediation%20neighbors%20discussion%20building%20apartment%20complex%20resolution%20dialogue&width=600&height=400&seq=svc006&orientation=landscape',
    tag: 'Comunidad',
  },
];

export default function Services() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>();

  return (
    <section ref={ref} id="servicios" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: isVisible ? '100ms' : '0ms' }}
        >
          <div>
            <span className="text-[#3D9E8C] text-sm font-semibold tracking-widest uppercase">Nuestros Servicios</span>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold text-[#1B3A5C] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Soluciones para cada<br />
              <em className="text-[#3D9E8C] not-italic">tipo de conflicto</em>
            </h2>
          </div>
          <p className="text-gray-500 text-[15px] max-w-sm leading-relaxed">
            Mediadores especializados en cada área para garantizar el mejor resultado en tu caso concreto.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, idx) => (
            <article
              key={s.title}
              className={`group rounded-2xl overflow-hidden border border-gray-100 hover:border-[#3D9E8C]/30 transition-all duration-700 ease-out bg-[#F9FBFC] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: isVisible ? `${200 + idx * 90}ms` : '0ms' }}
            >
              <div className="relative w-full h-48 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 text-[#3D9E8C] text-xs font-semibold rounded-full">
                  {s.tag}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#E8F4F1]">
                    <i className={`${s.icon} text-[#3D9E8C] text-lg`} />
                  </div>
                  <h3 className="font-bold text-[#1B3A5C] text-base">{s.title}</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{s.desc}</p>
                <ul className="flex flex-col gap-1.5">
                  {s.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-[#3D9E8C] text-base" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
